/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { ListItemText, Menu, MenuItem } from '@mui/material';
import React from 'react';
import { CustomToggleButton, sessionStorageProvider } from '@opengeoweb/shared';
import { Add } from '@opengeoweb/theme';
import type { WarningType } from '../../../store/warningsForm/types';
import { useWarningsTranslation } from '../../../utils/i18n';

export const BUTTON_AIRMET = 'AIRMET';
export const BUTTON_SIGMET = 'SIGMET';

export interface NewWarningButtonProps {
  onCreateWarning: (warningType?: WarningType) => void;
  isDefaultOpen?: boolean;
}

export const NewWarningButton: React.FC<NewWarningButtonProps> = ({
  onCreateWarning,
  isDefaultOpen = false,
}: NewWarningButtonProps) => {
  const { t } = useWarningsTranslation();
  const config = sessionStorageProvider.getConfig();
  const showMartimeWarningOption =
    !!config.GW_WARNINGS_MARITIME_WARNINGS && !!config.GW_DRAWINGS_BASE_URL;
  const showAirMetOption = !!config.GW_AIRMET_BASE_URL;
  const showSigmetOption = !!config.GW_SIGMET_BASE_URL;
  const showPublicWarningOption = !!config.GW_DRAWINGS_BASE_URL;

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const [menuMinWidth, setMenuMinWidth] = React.useState<number>(0);

  const buttonId = 'newWarningButton';

  React.useEffect(() => {
    if (isDefaultOpen) {
      setAnchorEl(document.getElementById(buttonId) as HTMLButtonElement);
    }
  }, [isDefaultOpen, buttonId]);

  const handleClick = (event: React.MouseEvent<HTMLElement, Event>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    const buttonWidth = event.currentTarget.getBoundingClientRect().width;
    setMenuMinWidth(buttonWidth);
  };

  const handleClose = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const onCreateNewWarning = (
    warningType: WarningType,
    event: React.MouseEvent,
  ): void => {
    onCreateWarning(warningType);
    handleClose(event);
  };

  return (
    <>
      <CustomToggleButton
        id={buttonId}
        onClick={handleClick}
        sx={{
          textTransform: 'initial!important',
          marginBottom: '8px',
          fontSize: '12px',
          height: '32px!important',
          whiteSpace: 'nowrap',
          minWidth: 'auto',
          paddingRight: '12px!important',
          ...(isOpen
            ? {
                '&.MuiToggleButton-root.primary': {
                  backgroundColor:
                    'geowebColors.buttons.primary.mouseOver.fill',
                },
              }
            : {}),
        }}
      >
        <Add />
        {t('warning-list-button_create')}
      </CustomToggleButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={(
          event: React.MouseEvent,
          reason: 'backdropClick' | 'escapeKeyDown' | 'tabKeyDown',
        ): void => {
          if (reason === 'tabKeyDown') {
            return;
          }
          handleClose(event);
        }}
        open={isOpen}
        slotProps={{
          root: {
            slotProps: {
              backdrop: {
                style: {
                  opacity: 0,
                  transition: 'none',
                },
              },
            },
          },
          list: {
            style: {
              padding: '0px',
              minWidth: menuMinWidth || undefined,
            },
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {showPublicWarningOption ? (
          <MenuItem
            key="Public"
            onClick={(event: React.MouseEvent): void => {
              onCreateNewWarning('public', event);
            }}
          >
            <ListItemText>
              {t('public-warning-title').toUpperCase()}
            </ListItemText>
          </MenuItem>
        ) : null}
        {showAirMetOption ? (
          <MenuItem
            key="Airmet"
            onClick={(event: React.MouseEvent): void => {
              onCreateNewWarning('airmet', event);
            }}
          >
            <ListItemText>{BUTTON_AIRMET}</ListItemText>
          </MenuItem>
        ) : null}
        {showSigmetOption ? (
          <MenuItem
            key="Sigmet"
            onClick={(event: React.MouseEvent): void => {
              onCreateNewWarning('sigmet', event);
            }}
          >
            <ListItemText>{BUTTON_SIGMET}</ListItemText>
          </MenuItem>
        ) : null}
        {showMartimeWarningOption ? (
          <>
            <MenuItem
              key="MaritimeWarningCoastal"
              onClick={(event: React.MouseEvent): void => {
                onCreateNewWarning('maritimeWarningCoastal', event);
              }}
            >
              <ListItemText>
                {t('maritime-warning-coastal-title').toUpperCase()}
              </ListItemText>
            </MenuItem>
            <MenuItem
              key="MaritimeWarningNorthSea"
              onClick={(event: React.MouseEvent): void => {
                onCreateNewWarning('maritimeWarningNorthSea', event);
              }}
            >
              <ListItemText>
                {t('maritime-warning-northsea-title').toUpperCase()}
              </ListItemText>
            </MenuItem>
          </>
        ) : null}
      </Menu>
    </>
  );
};
